import React, { useState, useEffect } from 'react';
import { AppContext } from '../../App';

import { ButtonBar } from '../ButtonBar';
import { Tree } from './tree';
import { RankingTable } from '../RankingTable';
import { dataFetcher } from '../../Utils/dataFetcher';
import TagManager from 'react-gtm-module';

let timeout;

/**
 * finds a specific heat in all rounds
 *
 * @param {[*]} rounds
 * @param {*} heat
 */
const findHeat = (rounds, heat) => {
  if (!rounds || !heat) {
    return;
  }

  for (let roundI = 0; roundI < rounds.length; roundI++) {
    if (rounds[roundI] && rounds[roundI].heats) {
      for (let heatsI = 0; heatsI < rounds[roundI].heats.length; heatsI++) {
        if (rounds[roundI].heats[heatsI].id === heat.id) {
          return rounds[roundI].heats[heatsI];
        }
      }
    }
  }
};

/**
 * Builds a knockout tree view
 */
const KnockOutTree = ({ config }) => {
  const context = React.useContext(AppContext);
  const uriConfig = context.config.uriConfig;

  const [buttons, setButtons] = useState(
    config.buttons.map((text, index) => {
      return { text: text, active: index === 0 };
    })
  );
  const [heat, setHeat] = useState();
  const [dataSet, setDataSet] = useState();

  const activeName = buttons.find(({ active }) => !!active).text;

  /**
   * set currently active heat when dataSet is changed
   */
  useEffect(() => {
    if (heat && heat.id && activeName && dataSet[activeName.toLowerCase()]) {
      const newHeat = findHeat(dataSet[activeName.toLowerCase()].rounds, heat);

      setHeat(newHeat);
    }
  }, [dataSet, activeName, heat]);

  const setData = () => {
    config.apiUris.forEach(async ({ forButton, uri }) => {
      const data = await dataFetcher(uri, uriConfig);
      if (data) {
        setDataSet((dataSet) => {
          return { ...dataSet, [forButton.toLowerCase()]: data };
        });
      }
    });

    timeout = setTimeout(
      setData,
      config.pollRateSeconds ? config.pollRateSeconds * 1000 : 15000
    );
  };

  /**
   * Manage API calls, updates when config is updated
   */
  useEffect(() => {
    if (timeout) {
      clearTimeout(timeout);
    }

    setData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  /**
   * cleanup when component will unmount
   */
  useEffect(() => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: 'loaded',
        widgetType: `Knockout Tree - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sets data to the tree specified by button
   *
   * @param {number} index
   */
  const buttonAction = (index) => {
    let oldIndex = 0;
    for (let checkIndex = 0; checkIndex < buttons.length; checkIndex++) {
      if (buttons[checkIndex].active === true) {
        oldIndex = checkIndex;
        break;
      }
    }

    if (oldIndex !== index) {
      setHeat(undefined);
    }

    const newButtons = buttons.map((button) => {
      return { ...button, active: false };
    });
    newButtons[index].active = true;

    setButtons(newButtons);

    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `${newButtons[index].text}`,
        widgetType: `Knockout Tree - ${config.trackingTitle || config.navigationTitle}`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
  };

  return (
    <>
      {dataSet && (
        <>
          {buttons.length > 1 ? <ButtonBar buttons={buttons} onClick={buttonAction} /> : <br />}
          <Tree
            data={
              dataSet[activeName.toLowerCase()]
                ? dataSet[activeName.toLowerCase()].rounds
                : []
            }
            setHeat={setHeat}
            activeHeat={heat}
            category={activeName}
            config={config}
          />
          <RankingTable
            heat={heat}
            data={
              dataSet[activeName.toLowerCase()]
                ? dataSet[activeName.toLowerCase()].rounds
                : []
            }
            config={config}
          />
        </>
      )}
    </>
  );
};

export { KnockOutTree };
